import React, { useState, useRef, useEffect } from 'react';

const SearchbarDestinataire = ({ 
  revendeurs = [], 
  carrossiers = [], 
  selectedValue, 
  onSelect, 
  placeholder = "Rechercher un destinataire..." 
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Combiner tous les destinataires avec leurs types
  const allDestinataires = [
    ...revendeurs.map(r => ({ ...r, type: 'revendeur', typeLabel: '🏪 Revendeur' })),
    ...carrossiers.map(c => ({ ...c, type: 'carrossier', typeLabel: '🔧 Carrossier' }))
  ];

  // Filtrer les résultats selon la recherche
  const filteredResults = allDestinataires.filter(dest => {
    const searchTerm = query.toLowerCase().trim();
    if (!searchTerm) return true;
    
    return (
      dest.company?.toLowerCase().includes(searchTerm) ||
      dest.email?.toLowerCase().includes(searchTerm) ||
      dest.contact?.toLowerCase().includes(searchTerm) ||
      dest.type.toLowerCase().includes(searchTerm)
    );
  });

  // Grouper par type pour un meilleur affichage
  const groupedResults = {
    revendeurs: filteredResults.filter(d => d.type === 'revendeur'),
    carrossiers: filteredResults.filter(d => d.type === 'carrossier')
  };

  // Obtenir le destinataire sélectionné pour l'affichage
  const selectedDestinataire = allDestinataires.find(d => d.id === selectedValue);

  // Navigation clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredResults.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredResults.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredResults[highlightedIndex]) {
            handleSelect(filteredResults[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredResults]);

  // Fermer la liste quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll automatique vers l'élément en surbrillance
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    
    // Si on efface tout, réinitialiser la sélection
    if (!value.trim()) {
      onSelect('');
    }
  };

  const handleSelect = (destinataire) => {
    setQuery(destinataire.company);
    onSelect(destinataire.id);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Si un destinataire est déjà sélectionné, le mettre dans la barre de recherche
    if (selectedDestinataire) {
      setQuery(selectedDestinataire.company);
    }
  };

  const clearSelection = () => {
    setQuery('');
    onSelect('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    searchRef.current?.focus();
  };

  const renderDestinataire = (dest, index) => {
    const isHighlighted = index === highlightedIndex;
    const isSelected = dest.id === selectedValue;
    
    return (
      <div
        key={dest.id}
        className={`
          p-3 cursor-pointer border-b border-gray-100 transition-all duration-150
          ${isHighlighted ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}
          ${isSelected ? 'bg-green-50 border-green-200' : ''}
        `}
        onClick={() => handleSelect(dest)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm">{dest.typeLabel}</span>
              <span className="font-medium text-gray-900">{dest.company}</span>
              {isSelected && <span className="text-green-600">✓</span>}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              📧 {dest.email}
              {dest.contact && (
                <span className="ml-3">👤 {dest.contact}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={searchRef} className="relative">
      {/* Barre de recherche */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`
            w-full p-3 border rounded-lg transition-all
            ${isOpen ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-300'}
            ${selectedValue ? 'bg-green-50 border-green-400' : 'bg-white'}
          `}
          autoComplete="off"
        />
        
        {/* Icônes à droite */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          {selectedValue && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Effacer la sélection"
            >
              ✕
            </button>
          )}
          <span className="text-gray-400">
            {isOpen ? '🔍' : '⬇️'}
          </span>
        </div>
      </div>

      {/* Informations sur la sélection */}
      {selectedDestinataire && !isOpen && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
          <div className="flex items-center gap-2">
            <span className="text-green-700">✅ Sélectionné :</span>
            <span className="font-medium">{selectedDestinataire.typeLabel}</span>
            <span className="text-gray-600">{selectedDestinataire.company}</span>
          </div>
        </div>
      )}

      {/* Liste déroulante des résultats */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {filteredResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <div className="text-lg mb-2">🔍</div>
              <div>Aucun destinataire trouvé</div>
              <div className="text-sm mt-1">
                Essayez avec le nom de l'entreprise ou l'email
              </div>
            </div>
          ) : (
            <div ref={listRef} className="max-h-80 overflow-y-auto">
              {/* Section Revendeurs */}
              {groupedResults.revendeurs.length > 0 && (
                <>
                  <div className="sticky top-0 bg-blue-100 px-3 py-2 text-sm font-semibold text-blue-800 border-b">
                    🏪 Revendeurs ({groupedResults.revendeurs.length})
                  </div>
                  {groupedResults.revendeurs.map((dest, idx) => 
                    renderDestinataire(dest, filteredResults.indexOf(dest))
                  )}
                </>
              )}

              {/* Section Carrossiers */}
              {groupedResults.carrossiers.length > 0 && (
                <>
                  <div className="sticky top-0 bg-orange-100 px-3 py-2 text-sm font-semibold text-orange-800 border-b">
                    🔧 Carrossiers ({groupedResults.carrossiers.length})
                  </div>
                  {groupedResults.carrossiers.map((dest, idx) => 
                    renderDestinataire(dest, filteredResults.indexOf(dest))
                  )}
                </>
              )}
            </div>
          )}

          {/* Footer avec raccourcis clavier */}
          {filteredResults.length > 0 && (
            <div className="border-t bg-gray-50 px-3 py-2 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <span>
                  💡 Utilisez ↑↓ pour naviguer, Entrée pour sélectionner
                </span>
                <span>
                  {filteredResults.length} résultat{filteredResults.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchbarDestinataire;